export const getBreakpointValue = (breakpoint) => {
  const breakpointValue = getComputedStyle(document.body).getPropertyValue(`--breakpoint-${breakpoint}`)
  return breakpointValue ? Number(breakpointValue.replace('px', '')) : undefined
}

export const updateURLSearchParams = (form) => {
  const formData = new FormData(form)
  const params = new URLSearchParams(formData)
  const url = new URL(window.location)
  url.search = ''
  params.forEach((value, key) => {
    if (value) url.searchParams.append(key, value)
  })
  history.pushState({}, '', url)
}

export const appendFormData = (data, obj, parentKey = '') => {
  Object.keys(obj).forEach((key) => {
    const fullKey = parentKey ? `${parentKey}[${key}]` : key
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      appendFormData(data, obj[key], fullKey)
    } else {
      data.append(fullKey, obj[key])
    }
  })
}

export const getPosts = async () => {
  const pagination = document.getElementById('pagination')
  const results = document.getElementById('results')
  const args = JSON.parse(results?.getAttribute('data-args') ?? '{}')
  const formData = new FormData()
  formData.append('action', 'get_posts')

  appendFormData(formData, { args })
  new URLSearchParams(window.location.search).forEach((value, key) => {
    formData.append(key, value)
  })

  formData.append('pathname', window.location.pathname)

  try {
    const response = await fetch(battcon_global.ajax_url, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

    const resData = await response.json()
    if (response.ok) {
      results.innerHTML = resData.data.results
      results.dispatchEvent(new CustomEvent('update'));
      if (pagination) pagination.innerHTML = resData.data.pagination
      document.querySelector('#results').scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  } catch (error) {
    console.error('Error:', error)
  }
}

export const getSessions = async (page = 1, scroll = true) => {
  const results = document.querySelector('.session-board__content')
  const paginationContainer = document.querySelector('.session-board__pagination-wrapper')
  const form = document.querySelector('.session-board form')

  if (!results || !form) return

  const formData = new FormData(form)
  formData.append('action', 'filter_sessions')
  formData.append('page', page)

  results.classList.add('loading')

  try {
    const response = await fetch(battcon_global.ajax_url, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

    const resData = await response.json()

    if (resData.success) {
      const availableDays = resData.data.availableDays || [];

      // Update desktop day button visibility
      document.querySelectorAll('.session-board__days__btn').forEach(btn => {
        const isUnavailable = availableDays.length > 0 && !availableDays.includes(btn.dataset.day);
        btn.classList.toggle('unavailable', isUnavailable);
      });

      // Update mobile day select label visibility
      const mobileSelect = document.querySelector('.session-board__days__select');
      if (mobileSelect) {
        mobileSelect.querySelectorAll('input[type="radio"]').forEach(radio => {
          const label = mobileSelect.querySelector(`label[for="${radio.id}"]`);
          if (label) {
            const isUnavailable = availableDays.length > 0 && !availableDays.includes(radio.value);
            label.classList.toggle('unavailable', isUnavailable);
          }
        });
      }

      if (availableDays.length === 0) {
        results.innerHTML = `
          <div class="session-board__no-results">
            <p class="session-board__no-results__title">No sessions found</p>
            <p class="session-board__no-results__sub">Try adjusting your filters</p>
            <button type="button" class="session-board__no-results__clear">Clear all filters</button>
          </div>
        `;
        results.querySelector('.session-board__no-results__clear').addEventListener('click', () => {
          document.querySelectorAll('.session-board form input[type="radio"]').forEach(r => {
            r.checked = false;
            r.dataset.wasChecked = 'false';
          });
          getSessions();
        });
        if (paginationContainer) paginationContainer.innerHTML = '';
      } else {
        results.innerHTML = resData.data.results;

        if (paginationContainer) {
          paginationContainer.innerHTML = resData.data.pagination;
          initPaginationButtons();
        }

        initExpandableText(results);
      }

      if (scroll) results.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      console.error('Error:', resData.data?.message || 'Unknown error')
    }
  } catch (error) {
    console.error('Error:', error)
  } finally {
    results.classList.remove('loading')
  }
}

export const initPaginationButtons = () => {
  const paginationButtons = document.querySelectorAll('.pagination-btn')

  paginationButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault()
      const page = parseInt(btn.getAttribute('data-page'))
      if (page) {
        getSessions(page)
      }
    })
  })
}

/**
 * Progressive disclosure for long SessionBoard copy (session descriptions,
 * presentation abstracts and speaker bios).
 *
 * Expects a `.js-expandable` block followed by its `.js-expandable-toggle`
 * button. The block is clamped with CSS so its HTML stays intact, and the
 * toggle is hidden when the copy already fits.
 */
export const initExpandableText = (root = document) => {
  const toggles = root.querySelectorAll('.js-expandable-toggle')

  for (const toggle of toggles) {
    if (toggle.dataset.expandableReady === 'true') continue

    const text = toggle.previousElementSibling
    if (!text || !text.classList.contains('js-expandable')) continue

    toggle.dataset.expandableReady = 'true'

    const labelMore = toggle.dataset.more || 'Show more'
    const labelLess = toggle.dataset.less || 'Show less'

    const collapse = () => {
      text.classList.add('is-clamped')
      toggle.setAttribute('aria-expanded', 'false')
      toggle.textContent = labelMore
    }

    const expand = () => {
      text.classList.remove('is-clamped')
      toggle.setAttribute('aria-expanded', 'true')
      toggle.textContent = labelLess
    }

    // Hide the toggle when the clamp isn't actually cutting anything off. Re-run
    // once webfonts have landed, since the first measurement can be off.
    const syncToggle = () => {
      if (toggle.getAttribute('aria-expanded') === 'true') return
      collapse()
      const fits = text.clientHeight > 0 && text.scrollHeight <= text.clientHeight + 1
      toggle.hidden = fits
      if (fits) text.classList.remove('is-clamped')
    }

    syncToggle()
    if (document.fonts?.ready) document.fonts.ready.then(syncToggle)

    toggle.addEventListener('click', (e) => {
      e.preventDefault()
      if (toggle.getAttribute('aria-expanded') === 'true') collapse()
      else expand()
    })
  }
}
